// Resource Timing collector.
//
// Cardinality is the enemy here: a single page can produce hundreds of
// resource entries. We:
//   - Sample at the configured resource_timing rate (default 10%).
//   - Skip our own beacons (don't observe the observer).
//   - Strip URL query strings and credentials before sending to drop tokens
//     and PII leaking via `?auth=...` patterns. The server may apply
//     additional redaction.

import { observePerf, now } from '../safe.js';
import { EVENT_TYPES } from '../schema.js';
import { shouldSample } from '../config.js';

const SENSITIVE_QUERY_KEYS = /(?:^|[?&;])(auth|token|access_token|id_token|api[-_]?key|sig|signature|password|secret)=/i;

export function initResources(viewIdProvider, emit, ingestEndpoint) {
  const own = ownHost(ingestEndpoint);

  observePerf('resource', (entries) => {
    for (const r of entries) {
      if (own && r.name && r.name.indexOf(own) === 0) continue;
      if (!shouldSample(EVENT_TYPES.RESOURCE_TIMING)) continue;
      emit(toEvent(r, viewIdProvider()));
    }
  });
}

function toEvent(r, viewId) {
  const cleanedUrl = sanitiseUrl(r.name);
  return {
    id: viewId + ':r:' + r.startTime.toFixed(0),
    t: now(),
    tp: EVENT_TYPES.RESOURCE_TIMING,
    n: 'resource',
    mv: r.duration || 0,
    mu: 'ms',
    mr: '',
    mnt: '',
    dim: {
      resource_render_blocking: r.renderBlockingStatus || ''
    },
    met: {},

    resource_url: cleanedUrl,
    resource_type: r.initiatorType || '',
    resource_initiator_type: r.initiatorType || '',
    resource_method: '',          // Resource Timing doesn't expose method; XHR/fetch hooks could.
    resource_status: typeof r.responseStatus === 'number' ? r.responseStatus : 0,
    resource_size_bytes: r.transferSize || r.encodedBodySize || 0,
    resource_duration_ms: r.duration || 0,
    resource_dns_ms: clamp(r.domainLookupEnd - r.domainLookupStart),
    resource_tcp_ms: clamp(r.connectEnd - r.connectStart),
    resource_ssl_ms: r.secureConnectionStart > 0 ? clamp(r.connectEnd - r.secureConnectionStart) : 0,
    resource_ttfb_ms: clamp(r.responseStart - r.requestStart),
    resource_download_ms: clamp(r.responseEnd - r.responseStart)
  };
}

function clamp(v) { return v > 0 ? v : 0; }

function sanitiseUrl(u) {
  if (!u) return '';
  try {
    const url = new URL(u, location.href);
    url.username = '';
    url.password = '';
    if (url.search && SENSITIVE_QUERY_KEYS.test(url.search)) {
      // Cheaper than parsing every key: if any sensitive key is present at
      // all, drop the whole query. The path is still useful for grouping.
      url.search = '';
    }
    return url.toString();
  } catch (_) {
    return String(u).split('?')[0];
  }
}

function ownHost(endpoint) {
  if (!endpoint) return '';
  try { return new URL(endpoint, location.href).origin; }
  catch (_) { return ''; }
}
